/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'

import { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, ProgressBar, Alert } from 'react-bootstrap';
import { useContext } from 'react';
import { MetadataContext, MetadataContextType } from './MetadataContext';
import FileList from './FileList';
import { WsContext, WsContextType } from './WsContext';
import { formatBytes } from '../utils/formatBytes';


interface LoadDialogProps {
    cloud: boolean;
    onLoad: (filename: string, cloud: boolean, progressId?: string) => Promise<void>;
    onCancel: () => void;
    isOpen: boolean;
    title?: string;
    primaryLabel?: string;
    fileFilter?: RegExp;
    defaultDirectoryChoice?: string;
    enableProgress?: boolean;
    progressOp?: string;
    preselectedFilename?: string;
    preselectedDirectory?: string;
}

const PRIVATE_DIR_KEY = '__private__';
const NON_PRIVATE_DIRS = ['public', 'library/dev', 'library/queue', 'library/originals', 'library/processed'];

function LoadDialog({
    onLoad,
    onCancel,
    isOpen,
    title,
    primaryLabel = 'Load',
    fileFilter = /\.fbot$/,
    defaultDirectoryChoice = PRIVATE_DIR_KEY,
    enableProgress = true,
    progressOp = 'load',
    preselectedFilename,
    preselectedDirectory,
} : LoadDialogProps) {
    const metadataContext = useContext<MetadataContextType>(MetadataContext);
    const wsContext = useContext<WsContextType>(WsContext);
    const isPlaceholder = (value?: string) => {
        if (!value) {
            return true;
        }
        const normalized = value.trim().toLowerCase();
        return normalized === '' || normalized === 'unknown' || normalized === 'loading';
    };
    const resolvedUserDir = metadataContext.user_dir;
    const privateDirectory = !isPlaceholder(resolvedUserDir)
        ? resolvedUserDir
        : 'user';
    const resolveDirectory = (choice: string) => (choice === PRIVATE_DIR_KEY ? privateDirectory : choice);
    const normalizeDirectoryChoice = (savedDir: string) => {
        const trimmed = (savedDir || '').trim();
        const normalized = trimmed.toLowerCase();
        if (trimmed === PRIVATE_DIR_KEY) {
            return PRIVATE_DIR_KEY;
        }
        if (NON_PRIVATE_DIRS.includes(trimmed)) {
            return trimmed;
        }
        if (isPlaceholder(trimmed) || normalized === 'user' || trimmed === privateDirectory) {
            return PRIVATE_DIR_KEY;
        }
        return PRIVATE_DIR_KEY;
    };
    const [filename, setFilename] = useState('');
    const [ cloud, setCloud ] = useState<boolean>(true);
    const [ directoryChoice, setDirectoryChoice ] = useState<string>(PRIVATE_DIR_KEY);
    const [ search, setSearch ] = useState<string>('');
    const [activeProgressId, setActiveProgressId] = useState<string | null>(null);
    const [progressNow, setProgressNow] = useState<number>(0);
    const [progressLabel, setProgressLabel] = useState<string>('');
    const [bytesTransferred, setBytesTransferred] = useState<number>(0);
    const [totalBytes, setTotalBytes] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const directory = resolveDirectory(directoryChoice);

    const makeProgressId = () => {
        return crypto.randomUUID();
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        try {
            const savedCloud = localStorage.getItem('fbot.load.cloud');
            if (savedCloud !== null) {
                setCloud(savedCloud === 'true');
            }
            const savedDir = localStorage.getItem('fbot.load.dir');
            if (savedDir) {
                setDirectoryChoice(normalizeDirectoryChoice(savedDir));
            } else {
                setDirectoryChoice(normalizeDirectoryChoice(defaultDirectoryChoice));
            }
        } catch (e) {
            // ignore localStorage errors
        }
        setActiveProgressId(null);
        setProgressNow(0);
        setProgressLabel('');
        setBytesTransferred(0);
        setTotalBytes(null);
        setIsLoading(false);
        setLoadError(null);
    }, [isOpen, privateDirectory]);

    useEffect(() => {
        if (!isOpen || !preselectedFilename) return;
        setFilename(preselectedFilename);
        if (preselectedDirectory) {
            setDirectoryChoice(normalizeDirectoryChoice(preselectedDirectory));
        }
    }, [isOpen, preselectedFilename, preselectedDirectory]);

    useEffect(() => {
        if (!wsContext.ws || !isOpen) {
            return;
        }
        const handler = (msg: any) => {
            if (!enableProgress || !activeProgressId || msg?.progressId !== activeProgressId || msg?.op !== progressOp) {
                return;
            }
            if (typeof msg?.now === 'number') {
                setProgressNow(Math.max(0, Math.min(100, msg.now)));
            }
            if (typeof msg?.bytesTransferred === 'number') {
                setBytesTransferred(Math.max(0, msg.bytesTransferred));
            }
            if (typeof msg?.totalBytes === 'number' && msg.totalBytes > 0) {
                setTotalBytes(msg.totalBytes);
            }
            setProgressLabel(msg?.label || 'Transferring...');
        };
        wsContext.ws.on('fileTransferProgress', handler);
        return () => {
            wsContext.ws.off('fileTransferProgress', handler);
        };
    }, [wsContext.ws, isOpen, activeProgressId, enableProgress, progressOp]);

    useEffect(() => {
        try {
            localStorage.setItem('fbot.load.cloud', cloud ? 'true' : 'false');
        } catch (e) {
            // ignore localStorage errors
        }
    }, [cloud]);

    useEffect(() => {
        try {
            localStorage.setItem('fbot.load.dir', directoryChoice);
        } catch (e) {
            // ignore localStorage errors
        }
    }, [directoryChoice]);

    if (!isOpen) return null;

    const handleLoad = async () => {
        if (filename.trim()) {
            const progressId = enableProgress ? makeProgressId() : undefined;
            setIsLoading(true);
            setLoadError(null);
            setActiveProgressId(progressId || null);
            setProgressNow(0);
            setProgressLabel(cloud ? 'Preparing download...' : 'Processing...');
            setBytesTransferred(0);
            setTotalBytes(null);
            const trimmed = filename.trim();
            try {
                if (trimmed.includes('/')) {
                    await onLoad(trimmed, cloud, progressId);
                    onCancel();
                    return;
                }
                const key = `${directory}/${trimmed}`;
                await onLoad(key, cloud, progressId);
                onCancel();
            } catch (err: any) {
                setLoadError(err?.message || 'Load failed');
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <Modal show={isOpen} onHide={onCancel} centered dialogClassName="modal-xx1">
            <Modal.Header closeButton>
            <Modal.Title>{title || 'Load'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
            <div className="mb-3">
                                <FileList cloud={cloud} prefix={directory} allowDelete={true} filter={fileFilter} showSearch={false} search={search} onSearchChange={setSearch}
                  clickFile={(fn : string) => {
                    setFilename(fn);
                 }} />
            </div>

            <div className="mb-3">
                <Row className="g-2 align-items-end">
                    <Col xs={12} md="auto">
                        <Form.Label>Origin</Form.Label>
                        <div className="btn-group w-100" role="group">
                            <Button
                                variant={cloud ? "primary" : "outline-primary"}
                                onClick={() => setCloud(true)}
                            >
                                Cloud
                            </Button>
                            <Button
                                variant={!cloud ? "primary" : "outline-primary"}
                                onClick={() => setCloud(false)}
                            >
                                Local
                            </Button>
                        </div>
                    </Col>
                    <Col xs={12} md>
                        <Form.Group controlId="directory">
                            <Form.Label>Directory</Form.Label>
                            <Form.Select
                                value={directoryChoice}
                                onChange={(e) => setDirectoryChoice(e.target.value)}
                                className="form-select"
                            >
                                <option value={PRIVATE_DIR_KEY}>{`Private (${privateDirectory})`}</option>
                                <option value="public">Public</option>
                                <option value="library/dev">Dev Library</option>
                                <option value="library/queue">Submission Queue</option>
                                <option value="library/originals">Original Submissions</option>
                                <option value="library/processed">Processed Submissions</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md>
                        <Form.Group controlId="search">
                            <Form.Label>Search</Form.Label>
                            <Form.Control
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search files..."
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            <Form.Group controlId="filename">
            <Form.Label>Filename:</Form.Label>
            <Form.Control
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="Enter filename"
            />
            </Form.Group>
            {loadError && (
                <Alert variant="danger" className="mt-3 mb-0">
                    {loadError}
                </Alert>
            )}
            </Modal.Body>
            <Modal.Footer>
                {enableProgress && activeProgressId && cloud && (
                    <div className="w-100 mb-2">
                        <div className="small mb-1">{progressLabel || 'Downloading...'}</div>
                        <div className="small text-muted mb-1">
                            {formatBytes(bytesTransferred)} / {totalBytes ? formatBytes(totalBytes) : 'unknown'}
                        </div>
                        <ProgressBar now={progressNow} label={`${Math.round(progressNow)}%`} animated={progressNow > 0 && progressNow < 100} />
                    </div>
                )}
                <Button variant="secondary" onClick={onCancel}>Cancel</Button>
                <Button variant="primary" onClick={handleLoad} disabled={isLoading}>{isLoading ? 'Loading...' : primaryLabel}</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default LoadDialog;